import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Location, LocationStatus } from "@/data/mockWarehousesLocations";
import { Edit, Power, Package, TrendingUp, Lock, Unlock, Clock, ArrowRightLeft, ArrowLeftRight, ShoppingCart, PackagePlus, ArrowDown, ArrowUp, Database, Trash2, Edit as EditIcon } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { getStockItems } from "@/services/stockDataService";
import { useMemo, useState } from "react";
import { useWarehouseHierarchyConfig } from "@/hooks/use-warehouse-hierarchy-config";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useNavigation } from "@/components/layout/app-layout";

interface LocationDetailsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  location: Location | null;
  onEdit: (location: Location) => void;
  onDeactivate: (location: Location) => void;
  onStatusChange?: (location: Location, newStatus: LocationStatus) => void;
  isLiveMode?: boolean;
  onDeactivateLocation?: (locationId: number) => Promise<{ success: boolean; error?: string }>;
  onBlockLocation?: (locationId: number) => Promise<{ success: boolean; error?: string }>;
  onUnblockLocation?: (locationId: number) => Promise<{ success: boolean; error?: string }>;
  onRefreshLocations?: () => void;
}

export function LocationDetailsDrawer({ 
  open, 
  onOpenChange, 
  location, 
  onEdit, 
  onDeactivate: _onDeactivate, 
  onStatusChange,
  isLiveMode = false,
  onDeactivateLocation,
  onBlockLocation,
  onUnblockLocation,
  onRefreshLocations
}: LocationDetailsDrawerProps) {
  // _onDeactivate is kept for backward compatibility but not used when live mode functions are provided
  void _onDeactivate;
  const { navigateTo } = useNavigation();
  const hierarchyConfig = useWarehouseHierarchyConfig({ isLive: isLiveMode });
  const [localLocation, setLocalLocation] = useState<Location | null>(location);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [deactivateError, setDeactivateError] = useState<string | null>(null);
  const [, setIsBlockLoading] = useState(false);
  const [, setIsDeactivateLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [isStockMovementDialogOpen, setIsStockMovementDialogOpen] = useState(false);

  
  // Update local location when prop changes
  useMemo(() => {
    setLocalLocation(location);
  }, [location]);
  
  // Get all stock items and filter by this location
  const stockItems = useMemo(() => getStockItems(), []);
  const locationStockItems = useMemo(() => {
    if (!localLocation) return [];
    return stockItems
      .filter(item => item.locationCode === localLocation.locationCode)
      .map(item => ({
        ...item,
        // Calculate unallocatedQuantity if not present
        unallocatedQuantity: item.unallocatedQuantity ?? (item.physicalQuantity - item.allocatedQuantity),
        // Calculate freeQuantity if not present (for now, same as unallocated)
        freeQuantity: item.freeQuantity ?? (item.physicalQuantity - item.allocatedQuantity)
      }));
  }, [stockItems, localLocation]);
  
  if (!localLocation) return null;
  
  // Handle block/unblock
  const handleToggleBlock = () => {
    setShowBlockConfirm(true);
  };
  
  const confirmToggleBlock = async () => {
    const isBlocking = localLocation.status !== "Blocked";
    
    if (isLiveMode && localLocation.id) {
      setIsBlockLoading(true);
      try {
        const result = isBlocking 
          ? await onBlockLocation?.(localLocation.id)
          : await onUnblockLocation?.(localLocation.id);
        
        if (result?.success) {
          const newStatus: LocationStatus = isBlocking ? "Blocked" : "Active";
          const updatedLocation = { ...localLocation, status: newStatus };
          setLocalLocation(updatedLocation);
          
          if (onStatusChange) {
            onStatusChange(updatedLocation, newStatus);
          }
          
          if (isBlocking) {
            toast.error(`Location ${localLocation.locationCode} has been blocked. No stock movements allowed.`);
          } else {
            toast.success(`Location ${localLocation.locationCode} has been unblocked. Stock movements are now allowed.`);
          }
          
          onRefreshLocations?.();
        } else {
          toast.error(result?.error || `Failed to ${isBlocking ? 'block' : 'unblock'} location`);
        }
      } catch {
        toast.error(`Failed to ${isBlocking ? 'block' : 'unblock'} location`);
      } finally {
        setIsBlockLoading(false);
      }
    } else {
      const newStatus: LocationStatus = localLocation.status === "Blocked" ? "Active" : "Blocked";
      const updatedLocation = { ...localLocation, status: newStatus };
      setLocalLocation(updatedLocation);
      
      if (onStatusChange) {
        onStatusChange(updatedLocation, newStatus);
      }
      
      if (newStatus === "Blocked") {
        toast.error(`Location ${localLocation.locationCode} has been blocked. No stock movements allowed.`);
      } else {
        toast.success(`Location ${localLocation.locationCode} has been unblocked. Stock movements are now allowed.`);
      }
    }
    
    setShowBlockConfirm(false);
  };

  // Handle deactivate/activate button click
  const handleDeactivateClick = () => {
    if (!localLocation) return;

    // If location is inactive, activate it
    if (localLocation.status === "Inactive") {
      setShowDeactivateConfirm(true);
      setDeactivateError(null);
      return;
    }

    // Check if location has stock items
    if (locationStockItems.length > 0) {
      setDeactivateError(`Cannot deactivate location. It contains ${locationStockItems.length} stock item(s). Please move all stock before deactivating.`);
      setShowDeactivateConfirm(true);
      return;
    }

    // Location is empty, show confirmation
    setDeactivateError(null);
    setShowDeactivateConfirm(true);
  };

  const confirmDeactivate = async () => {
    if (!localLocation) return;

    // If there's an error (has stock), don't proceed
    if (deactivateError) {
      setShowDeactivateConfirm(false);
      return;
    }

    const isDeactivating = localLocation.status !== "Inactive";

    if (isLiveMode && localLocation.id && isDeactivating) {
      setIsDeactivateLoading(true);
      try {
        const result = await onDeactivateLocation?.(localLocation.id);
        
        if (result?.success) {
          const newStatus: LocationStatus = "Inactive";
          const updatedLocation = { ...localLocation, status: newStatus };
          setLocalLocation(updatedLocation);
          
          if (onStatusChange) {
            onStatusChange(updatedLocation, newStatus);
          }
          
          toast.success(`Location ${localLocation.locationCode} has been deactivated.`);
          onRefreshLocations?.();
        } else {
          toast.error(result?.error || "Failed to deactivate location");
        }
      } catch {
        toast.error("Failed to deactivate location");
      } finally {
        setIsDeactivateLoading(false);
      }
    } else {
      // Demo mode or activating (activation not supported in live mode yet)
      const newStatus: LocationStatus = localLocation.status === "Inactive" ? "Active" : "Inactive";
      const updatedLocation = { ...localLocation, status: newStatus };
      setLocalLocation(updatedLocation);

      if (onStatusChange) {
        onStatusChange(updatedLocation, newStatus);
      }

      if (newStatus === "Inactive") {
        toast.success(`Location ${localLocation.locationCode} has been deactivated.`);
      } else {
        toast.success(`Location ${localLocation.locationCode} has been activated.`);
      }
    }

    setShowDeactivateConfirm(false);
    setDeactivateError(null);
  };
  
  // Get status badge variant
  const getStatusVariant = (status: LocationStatus) => {
    switch (status) {
      case "Active": return "success";
      case "Blocked": return "destructive";
      case "Inactive": return "secondary";
      default: return "secondary";
    }
  };
  
  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  };

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(value);
  };

  // Get hierarchy values
  const getHierarchyValue = (level: number): string => {
    // Try hierarchyData first
    if (location.hierarchyData?.[level]) {
      return location.hierarchyData[level];
    }
    // Fallback to legacy fields
    if (level === 1) return location.aisle || "-";
    if (level === 2) return location.rack || "-";
    if (level === 3) return location.shelf || "-";
    if (level === 4) return location.bin || "-";
    return "-";
  };

  // Handle checkbox selection
  const handleSelectAll = (checked: boolean | "indeterminate") => {
    if (checked === true) {
      setSelectedItems(locationStockItems.map(item => item.code));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (itemCode: string, checked: boolean | "indeterminate") => {
    if (checked === true) {
      setSelectedItems(prev => [...prev, itemCode]);
    } else {
      setSelectedItems(prev => prev.filter(code => code !== itemCode));
    }
  };

  // Handle stock movement
  const handleStockMovement = () => {
    // Open the stock movement dialog
    setIsStockMovementDialogOpen(true);
  };

  const handleMovementTypeSelect = (type: string) => {
    // Store selected items and source location in sessionStorage
    const selectedItemsData = locationStockItems
      .filter(item => selectedItems.includes(item.code))
      .map(item => ({
        code: item.code,
        description: item.description,
        quantity: item.physicalQuantity,
        sourceLocation: localLocation.locationCode
      }));

    const dataToStore = {
      sourceLocation: localLocation.locationCode,
      items: selectedItemsData
    };
    
    console.log('💾 Storing prefilled transfer data:', dataToStore);
    sessionStorage.setItem('prefilledTransfer', JSON.stringify(dataToStore));

    setIsStockMovementDialogOpen(false);

    // Navigate based on movement type
    if (type === 'Stock Transfer') {
      navigateTo('stock-transfers/create/step1');
    } else {
      navigateTo('add-movement', { type });
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:w-[700px] sm:max-w-[700px] p-0 flex flex-col">
        {/* Header */}
        <SheetHeader className="bg-muted p-4 space-y-3">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <SheetTitle className="text-2xl font-semibold font-mono">{localLocation.locationCode}</SheetTitle>
              <SheetDescription className="mt-1 space-y-2">
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{localLocation.locationType}</Badge>
                  {localLocation.status === "Blocked" ? (
                    <Badge className="bg-red-500 text-white hover:bg-red-600">
                      {localLocation.status}
                    </Badge>
                  ) : localLocation.status === "Inactive" ? (
                    <Badge className="bg-gray-500 text-white hover:bg-gray-600">
                      {localLocation.status}
                    </Badge>
                  ) : (
                    <Badge variant={getStatusVariant(localLocation.status)}>
                      {localLocation.status}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  <span>Last activity: {formatDate(localLocation.lastActivityDate)}</span>
                </div>
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* Hierarchy */}
          <div>
            <h3 className="text-sm font-semibold mb-3">Location Hierarchy</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {hierarchyConfig.activeLevels.map((level) => (
                <div key={level.level}>
                  <span className="text-muted-foreground">{level.displayName}:</span>
                  <span className="ml-2 font-mono font-medium">{getHierarchyValue(level.level)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Picking Sequence */}
          {localLocation.pickingSequence && (
            <div>
              <h3 className="text-sm font-semibold mb-3">Picking Optimisation</h3>
              <div className="text-sm">
                <span className="text-muted-foreground">Picking Sequence:</span>
                <span className="ml-2 font-mono font-medium">{localLocation.pickingSequence}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Lower numbers are picked first for optimised warehouse flow
              </p>
            </div>
          )}

          {/* Notes */}
          {localLocation.notes && (
            <div>
              <h3 className="text-sm font-semibold mb-3">Notes</h3>
              <div className="text-sm bg-muted p-3 rounded-lg">
                {localLocation.notes}
              </div>
            </div>
          )}

          {/* Stock Items at Location */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Package className="h-4 w-4" />
                Stock Items at Location
                <Badge variant="secondary">
                  {locationStockItems.length} {locationStockItems.length === 1 ? 'item' : 'items'}
                </Badge>
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={handleStockMovement}
                disabled={selectedItems.length === 0}
                className="gap-2"
              >
                <ArrowRightLeft className="h-4 w-4" />
                Stock Movement
                {selectedItems.length > 0 && (
                  <Badge variant="secondary" className="ml-1">
                    {selectedItems.length}
                  </Badge>
                )}
              </Button>
            </div>
            {locationStockItems.length > 0 ? (
              <div className="rounded-lg border overflow-hidden">
                <div className="max-h-[400px] overflow-y-auto">
                  <Table>
                    <TableHeader className="sticky top-0 bg-background z-10">
                      <TableRow>
                        <TableHead className="w-12">
                          <Checkbox
                            checked={selectedItems.length === locationStockItems.length && locationStockItems.length > 0}
                            onCheckedChange={handleSelectAll}
                          />
                        </TableHead>
                        <TableHead className="font-semibold">Code</TableHead>
                        <TableHead className="font-semibold">Description</TableHead>
                        <TableHead className="font-semibold text-right">Physical Qty</TableHead>
                        <TableHead className="font-semibold text-right">Allocated</TableHead>
                        <TableHead className="font-semibold text-right">Unallocated</TableHead>
                        <TableHead className="font-semibold text-right">Free Qty</TableHead>
                        <TableHead className="font-semibold text-right">Due In</TableHead>
                        <TableHead className="font-semibold text-right">Value</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {locationStockItems.map((item, index) => (
                        <TableRow key={item.code} className={index % 2 === 0 ? "bg-muted/30" : ""}>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedItems.includes(item.code)}
                              onCheckedChange={(checked) => handleSelectItem(item.code, checked)}
                            />
                          </TableCell>
                          <TableCell className="font-medium font-mono text-xs">{item.code}</TableCell>
                          <TableCell className="text-xs">{item.description}</TableCell>
                          <TableCell className="text-right font-mono text-xs">
                            {(item.physicalQuantity || 0).toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs">
                            {(item.allocatedQuantity || 0).toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs">
                            {(item.unallocatedQuantity || 0).toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs">
                            {(item.freeQuantity || 0).toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs">
                            {(item.quantityDueIn || 0).toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs font-semibold">
                            {formatCurrency(item.value || 0)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {/* Summary */}
                <div className="border-t bg-muted/50 p-3">
                  <Table>
                    <TableBody>
                      <TableRow className="border-0">
                        <TableCell className="font-semibold text-xs py-2" colSpan={2}>Totals:</TableCell>
                        <TableCell className="text-right font-mono text-xs font-semibold py-2">
                          {locationStockItems.reduce((sum, item) => sum + (item.physicalQuantity || 0), 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs font-semibold py-2">
                          {locationStockItems.reduce((sum, item) => sum + (item.allocatedQuantity || 0), 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs font-semibold py-2">
                          {locationStockItems.reduce((sum, item) => sum + (item.unallocatedQuantity || 0), 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs font-semibold py-2">
                          {locationStockItems.reduce((sum, item) => sum + (item.freeQuantity || 0), 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs font-semibold py-2">
                          {locationStockItems.reduce((sum, item) => sum + (item.quantityDueIn || 0), 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs font-semibold py-2">
                          {formatCurrency(locationStockItems.reduce((sum, item) => sum + (item.value || 0), 0))}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border p-4 text-center text-sm text-muted-foreground">
                <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No stock items at this location</p>
              </div>
            )}
          </div>

          {/* Recent Movements */}
          <div>
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Recent Movements
            </h3>
            <div className="rounded-lg border p-4 text-center text-sm text-muted-foreground">
              <p>Movement history coming soon</p>
              <p className="text-xs mt-1">Will show last 20 stock movements</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <SheetFooter className="bg-muted p-3 border-t flex-row justify-between gap-2">
          <div className="flex gap-2">
            <Button
              variant={localLocation.status === "Blocked" ? "outline" : "destructive"}
              onClick={handleToggleBlock}
              size="sm"
            >
              {localLocation.status === "Blocked" ? (
                <>
                  <Unlock className="h-4 w-4 mr-2" />
                  Unblock
                </>
              ) : (
                <>
                  <Lock className="h-4 w-4 mr-2" />
                  Block
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={handleDeactivateClick}
              disabled={localLocation.status === "Blocked"}
              size="sm"
            >
              <Power className="h-4 w-4 mr-2" />
              {localLocation.status === "Inactive" ? "Activate" : "Deactivate"}
            </Button>
          </div>
          <Button onClick={() => onEdit(localLocation)} size="sm">
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
        </SheetFooter>
      </SheetContent>
      
      {/* Block/Unblock Confirmation Dialog */}
      <AlertDialog open={showBlockConfirm} onOpenChange={setShowBlockConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {localLocation.status === "Blocked" ? "Unblock Location?" : "Block Location?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {localLocation.status === "Blocked" ? (
                <>
                  Are you sure you want to unblock <strong>{localLocation.locationCode}</strong>?
                  <br /><br />
                  This will allow stock movements to and from this location.
                </>
              ) : (
                <>
                  Are you sure you want to block <strong>{localLocation.locationCode}</strong>?
                  <br /><br />
                  This will prevent any stock movements to or from this location until it is unblocked.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmToggleBlock}>
              {localLocation.status === "Blocked" ? "Unblock" : "Block"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Deactivate/Activate Confirmation Dialog */}
      <AlertDialog open={showDeactivateConfirm} onOpenChange={setShowDeactivateConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deactivateError ? (
                "Cannot Deactivate Location"
              ) : localLocation.status === "Inactive" ? (
                "Activate Location?"
              ) : (
                "Deactivate Location?"
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deactivateError ? (
                <div className="space-y-3">
                  <p className="text-destructive font-medium">{deactivateError}</p>
                  <p className="text-sm">
                    Move all stock items to other locations before deactivating this location.
                  </p>
                </div>
              ) : localLocation.status === "Inactive" ? (
                <>
                  Are you sure you want to activate <strong>{localLocation.locationCode}</strong>?
                  <br /><br />
                  This will make the location available for stock movements again.
                </>
              ) : (
                <>
                  Are you sure you want to deactivate <strong>{localLocation.locationCode}</strong>?
                  <br /><br />
                  This location is empty and can be safely deactivated. You can activate it again later if needed.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {deactivateError ? "Close" : "Cancel"}
            </AlertDialogCancel>
            {!deactivateError && (
              <AlertDialogAction onClick={confirmDeactivate}>
                {localLocation.status === "Inactive" ? "Activate" : "Deactivate"}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Stock Movement Dialog */}
      <Dialog open={isStockMovementDialogOpen} onOpenChange={setIsStockMovementDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Create Stock Movement</DialogTitle>
            <DialogDescription>
              Select the type of stock movement you want to create
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 py-4">
            <Button
              variant="outline"
              className="h-auto py-4 px-4 flex items-start gap-3 hover:bg-blue-50 hover:border-blue-300"
              onClick={() => handleMovementTypeSelect('Sales')}
            >
              <div className="rounded-full bg-blue-100 p-2">
                <ArrowDown className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1 text-left">
                <div className="font-semibold">Sales</div>
                <div className="text-sm text-muted-foreground">Record stock sold to customers</div>
              </div>
            </Button>

            <Button
              variant="outline"
              className="h-auto py-4 px-4 flex items-start gap-3 hover:bg-green-50 hover:border-green-300"
              onClick={() => handleMovementTypeSelect('Purchase')}
            >
              <div className="rounded-full bg-green-100 p-2">
                <ArrowUp className="h-5 w-5 text-green-600" />
              </div>
              <div className="flex-1 text-left">
                <div className="font-semibold">Purchase</div>
                <div className="text-sm text-muted-foreground">Record stock received from suppliers</div>
              </div>
            </Button>

            <Button
              variant="outline"
              className="h-auto py-4 px-4 flex items-start gap-3 hover:bg-teal-50 hover:border-teal-300"
              onClick={() => handleMovementTypeSelect('Stock Transfer')}
            >
              <div className="rounded-full bg-teal-100 p-2">
                <ArrowRightLeft className="h-5 w-5 text-teal-600" />
              </div>
              <div className="flex-1 text-left">
                <div className="font-semibold">Stock Transfer</div>
                <div className="text-sm text-muted-foreground">Move stock between locations</div>
              </div>
            </Button>

            <Button
              variant="outline"
              className="h-auto py-4 px-4 flex items-start gap-3 hover:bg-purple-50 hover:border-purple-300"
              onClick={() => handleMovementTypeSelect('Opening Balance')}
            >
              <div className="rounded-full bg-purple-100 p-2">
                <Database className="h-5 w-5 text-purple-600" />
              </div>
              <div className="flex-1 text-left">
                <div className="font-semibold">Opening Balance</div>
                <div className="text-sm text-muted-foreground">Set initial stock quantities</div>
              </div>
            </Button>

            <Button
              variant="outline"
              className="h-auto py-4 px-4 flex items-start gap-3 hover:bg-red-50 hover:border-red-300"
              onClick={() => handleMovementTypeSelect('Write-off')}
            >
              <div className="rounded-full bg-red-100 p-2">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <div className="flex-1 text-left">
                <div className="font-semibold">Write-off</div>
                <div className="text-sm text-muted-foreground">Remove damaged or obsolete stock</div>
              </div>
            </Button>

            <Button
              variant="outline"
              className="h-auto py-4 px-4 flex items-start gap-3 hover:bg-gray-50 hover:border-gray-300"
              onClick={() => handleMovementTypeSelect('Manual Adjustment')}
            >
              <div className="rounded-full bg-gray-100 p-2">
                <EditIcon className="h-5 w-5 text-gray-600" />
              </div>
              <div className="flex-1 text-left">
                <div className="font-semibold">Manual Adjustment</div>
                <div className="text-sm text-muted-foreground">Correct stock quantities</div>
              </div>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Sheet>
  );
}
